import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ChevronLeft, ChevronRight, ChevronDown, X } from 'lucide-react';
import { menuSections, matchesPrefix, sectionRoot } from '../navConfig';
import useIsMobile from '../hooks/useIsMobile';
import { t } from '../i18n';

const apiUrl = process.env.REACT_APP_API_URL || '';

/**
 * Sidebar navigation.
 *
 * Desktop (md+): always visible, toggles between a 192px full rail and a 64px
 * icon-only rail. The content area is offset by a matching margin.
 * Mobile: off-canvas. Hidden until the header hamburger opens it, then it
 * slides over the content with a backdrop and closes on navigation. The
 * content area gets no margin, so it uses the full screen width.
 */
function Menu({ isOpen, setIsOpen, mobileOpen, setMobileOpen }) {
  const [expanded, setExpanded] = useState({});
  // Branding is cached so a refresh paints the company logo immediately
  // instead of flashing the Pulse fallback while /business-settings loads.
  const [branding, setBranding] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem('companyBranding'));
    } catch {
      return null;
    }
  });
  const [brandingLoaded, setBrandingLoaded] = useState(branding !== null);
  const [logoVersion, setLogoVersion] = useState(0);
  const location = useLocation();
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  // Show the company logo/name from business settings; fall back to the
  // Pulse branding when none is set. MyBusiness fires `business-logo-updated`
  // after an upload so the sidebar refreshes without a page reload.
  React.useEffect(() => {
    let cancelled = false;
    const fetchBranding = async () => {
      const token = localStorage.getItem('token');
      if (!token) {
        setBrandingLoaded(true);
        return;
      }
      try {
        const response = await fetch(`${apiUrl}/business-settings`, {
          headers: { 'Authorization': `Bearer ${token}` },
        });
        if (response.ok) {
          const data = await response.json();
          const next = { logo: data.logo || null, name: data.name || '' };
          if (!cancelled) setBranding(next);
          localStorage.setItem('companyBranding', JSON.stringify(next));
        }
      } catch {
        // Keep whatever branding we already have
      } finally {
        if (!cancelled) setBrandingLoaded(true);
      }
    };
    fetchBranding();
    const onLogoUpdated = () => {
      // Bump the version so the <img> URL changes and the browser refetches
      setLogoVersion(Date.now());
      fetchBranding();
    };
    window.addEventListener('business-logo-updated', onLogoUpdated);
    return () => {
      cancelled = true;
      window.removeEventListener('business-logo-updated', onLogoUpdated);
    };
  }, []);

  // Stored logos are API-relative paths (/storage/...); older values may be
  // data URLs, which must be used as-is. Until the first fetch (or cache)
  // resolves, render nothing rather than the wrong logo.
  let logoSrc = null;
  if (branding?.logo) {
    logoSrc = branding.logo.startsWith('/storage/')
      ? `${apiUrl}${branding.logo}${logoVersion ? `?v=${logoVersion}` : ''}`
      : branding.logo;
  } else if (brandingLoaded) {
    logoSrc = '/favicon.ico';
  }
  const brandName = branding?.name || (brandingLoaded ? 'Pulse' : '');

  // On a phone the panel is always the full-width variant; `isOpen` only
  // describes the desktop rail.
  const showLabels = isMobile || isOpen;

  const toggleSection = (section) => {
    setExpanded(prev => ({ ...prev, [section]: !prev[section] }));
  };

  // Auto-expand section based on current path
  React.useEffect(() => {
    menuSections.forEach(section => {
      if (matchesPrefix(location.pathname, section.prefix) ||
          (section.children && section.children.some(c => location.pathname === c.href))) {
        setExpanded(prev => ({ ...prev, [section.name]: true }));
      }
    });
  }, [location.pathname]);

  // Navigating on a phone should dismiss the overlay, otherwise it covers the
  // page the user just asked for.
  React.useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname, setMobileOpen]);

  React.useEffect(() => {
    if (!mobileOpen) return undefined;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') setMobileOpen(false);
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [mobileOpen, setMobileOpen]);

  const topSections = menuSections.filter((s) => !s.bottom);
  const bottomSections = menuSections.filter((s) => s.bottom);

  const renderSection = (section) => {
    // Simple link (no children)
    if (!section.children) {
      return (
        <Link
          key={section.name}
          to={section.href}
          title={t(section.name)}
          className={`flex items-center gap-3 px-3 py-2 rounded-full transition text-sm ${
            location.pathname === section.href
              ? 'bg-white text-blue-700 font-medium'
              : 'text-blue-100 hover:bg-blue-600 hover:text-white'
          }`}
        >
          {section.icon}
          {showLabels && t(section.name)}
        </Link>
      );
    }

    // Expandable section
    const isActive = section.children.some(c => location.pathname === c.href) ||
      matchesPrefix(location.pathname, section.prefix);

    const handleSectionClick = () => {
      if (showLabels) {
        toggleSection(section.name);
        return;
      }
      // Collapsed desktop rail: no room for a submenu, so go to the section
      // root. Navigating with the raw `prefix` sent React Router the string
      // "/products,/categories,..." when it was an array, matching no route.
      const root = sectionRoot(section);
      if (root) navigate(root);
    };

    return (
      <div key={section.name}>
        <button
          onClick={handleSectionClick}
          title={t(section.name)}
          className={`flex items-center gap-3 px-3 py-2 rounded-full transition text-sm w-full text-left ${
            isActive
              ? 'bg-white text-blue-700 font-medium'
              : 'text-blue-100 hover:bg-blue-600 hover:text-white'
          }`}
        >
          {section.icon}
          {showLabels && (
            <>
              <span className="flex-1">{t(section.name)}</span>
              <ChevronDown size={14} className={`transition-transform ${expanded[section.name] ? 'rotate-180' : ''}`} />
            </>
          )}
        </button>
        {expanded[section.name] && showLabels && (
          <div className="flex flex-col space-y-1 pl-4 mt-1">
            {section.children.map(({ name, href, icon }) => (
              <Link
                key={name}
                to={href}
                title={t(name)}
                className={`flex items-center gap-3 px-3 py-2 rounded-full transition text-sm ${
                  location.pathname === href
                    ? 'bg-white text-blue-700 font-medium'
                    : 'text-blue-100 hover:bg-blue-600 hover:text-white'
                }`}
              >
                {icon}
                {t(name)}
              </Link>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      {/* Backdrop — mobile only, and only while the panel is open. */}
      {mobileOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-30 md:hidden"
          onClick={() => setMobileOpen(false)}
          aria-hidden="true"
        />
      )}

      <aside
        className={`h-screen bg-blue-700 text-white fixed top-0 left-0 shadow-lg flex flex-col z-40
          overflow-y-auto transition-transform duration-300
          w-64 ${mobileOpen ? 'translate-x-0' : '-translate-x-full'}
          md:translate-x-0 md:transition-all ${isOpen ? 'md:w-48' : 'md:w-16'}`}
      >
        {/* Header with toggle */}
        <div className="h-16 flex items-center justify-between px-4 border-b border-blue-600 shrink-0">
          {/* The logo doubles as the way back to the dashboard. */}
          <Link
            to="/"
            onClick={() => isMobile && setMobileOpen(false)}
            title={t('Почетна страна')}
            className="flex items-center space-x-2 min-w-0 rounded hover:opacity-80 transition-opacity"
          >
            {logoSrc ? (
              <img
                src={logoSrc}
                alt={t('Лого')}
                className="w-8 h-8 shrink-0 object-contain"
              />
            ) : (
              // Reserve the logo's spot while branding loads, so nothing shifts
              <div className="w-8 h-8 shrink-0" />
            )}
            {showLabels && (
              <div className="flex flex-col leading-tight min-w-0">
                <span className="text-lg font-bold text-white truncate">{brandName}</span>
                <span className="text-[9px] text-blue-200 whitespace-nowrap">{t('овозможено од PulseFinance')}</span>
              </div>
            )}
          </Link>

          {/* Phone: dismiss the overlay. Desktop: collapse the rail. */}
          <button
            onClick={() => (isMobile ? setMobileOpen(false) : setIsOpen(!isOpen))}
            className="text-blue-200 hover:text-white"
            title={isMobile ? t('Затвори мени') : isOpen ? t('Собери мени') : t('Прошири мени')}
          >
            {isMobile ? <X size={20} /> : isOpen ? <ChevronLeft size={20} /> : <ChevronRight size={20} />}
          </button>
        </div>

        {/* Navigation Links */}
        <nav className="flex flex-col px-2 mt-4 space-y-1">
          {topSections.map(renderSection)}
        </nav>

        {/* Bottom-pinned links */}
        {bottomSections.length > 0 && (
          <nav className="flex flex-col px-2 mt-auto mb-4 pt-4 space-y-1 border-t border-blue-600">
            {bottomSections.map(renderSection)}
          </nav>
        )}
      </aside>
    </>
  );
}

export default Menu;
